// Dependencies
import React from "react"
import { graphql, Link } from "gatsby"
import PropTypes from "prop-types"
import { useLocation } from "@reach/router"
// Hooks
import useShare from "../hooks/useShare"
// Components
import Accordian from "../components/Accordian"
import AnnotatedText from "../components/AnnotatedText"
import Layout from "../components/Layout"
import Header from "../components/Header"
import { Image } from "@unpic/react"
import RichText, { getPlainText } from "../components/RichText"
import Markdown from "../components/Markdown"
import Footer from "../components/Footer"
import NewsletterForm from "../components/NewsletterForm"
import Vimeo from "../components/Vimeo"
import { resolveBlogLink } from "./blog-index"
import { FacebookShareButton } from "react-share"

const TwitterShareLink = ({ text, url }) => {
  const truncateQuote = text.length > 180 ? `${text.substring(0, 180)}…` : text
  const tweetParams = encodeURI(`text=${truncateQuote}\n\n&url=${url}`)

  return (
    <a
      href={`https://twitter.com/intent/tweet?${tweetParams}`}
      className="dark-link"
      target="_blank"
      rel="noopener noreferrer"
    >
      Twitter
    </a>
  )
}

const ArticleCoverImage = ({ coverImage }) => {
  // Random rotate transform between -4 and 4
  const rotate = Math.random() * 4 - 2

  return (
    <div
      className="article-cover-image innerx2"
      style={{ transform: `rotate(${rotate}deg)` }}
    >
      <Image
        src={
          coverImage?.file?.url ? `${coverImage?.file?.url}?w=1440&fm=webp` : ""
        }
      />
    </div>
  )
}

const AsideLink = ({ category, slug, summary, formattedDate, title }) => {
  const shortSummary =
    summary?.text?.length > 170
      ? `${summary.text.substring(0, 170)}…`
      : summary?.text ?? title
  return (
    <p className="hover:opacity-50">
      <Link to={resolveBlogLink(category.slug, slug)}>{shortSummary}</Link>
      <div className="opacity-50">{formattedDate}</div>
    </p>
  )
}

const ArticleTemplate = ({ data }) => {
  const {
    title,
    date,
    formattedDate,
    introduction,
    category,
    coverImage,
    body,
  } = data.contentfulBlogArticle

  const plainText = introduction ? getPlainText(introduction.raw) : title

  const moreArticles =
    data.relatedArticles.nodes.length > 0
      ? data.relatedArticles.nodes
      : data.recentArticles.nodes

  const location = useLocation()
  const shareUrl = `https://www.paulsmithsfoundation.com${location.pathname}`

  // UI
  const { share, hasShared } = useShare({ url: shareUrl })

  return (
    <Layout title={title}>
      <Header />
      <div className="article-page container">
        <div className="article-grid">
          <article className="col-4">
            <hgroup>
              <hr className="mbx2 pad-0 hide-mobile" />
              <time dateTime={date}>{formattedDate}</time>
              <h1 className="h3">{title}</h1>
            </hgroup>
            {introduction && (
              <AnnotatedText
                className="h0 article-introduction"
                richText={introduction}
                // field={section.copy}
              />
            )}
            {coverImage && <ArticleCoverImage coverImage={coverImage} />}
            <RichText
              className="article-content has-dark-links"
              content={body}
            />
          </article>
          <aside className="col-2 article-aside">
            <div className="article-aside-inner">
              <p>More in {category.title}</p>
              {moreArticles.map((article, index) => (
                <AsideLink {...article} key={article.slug} />
              ))}
              <div className="has-dark-links">
                <Link to={`/blog/${category.slug}`}>View all</Link>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <div className="container p3">
        <div className="advice-info-section has-border">
          <div>SHARE:</div>
          <div className="advice-info-tags">
            <span>
              <FacebookShareButton
                className="dark-link share-link"
                resetButtonStyle={false}
                url={shareUrl}
                quote={plainText}
              >
                Facebook
              </FacebookShareButton>
            </span>
            <span>
              <TwitterShareLink text={plainText} url={shareUrl} />
            </span>
            <span>
              <button className="dark-link" onClick={share}>
                {hasShared || "Copy Link"}
              </button>
            </span>
          </div>
        </div>
      </div>

      <Footer />
    </Layout>
  )
}

export default ArticleTemplate

export const pageQuery = graphql`
  query ArticleTemplateQuery($slug: String!, $categorySlug: String!) {
    relatedArticles: allContentfulBlogArticle(
      limit: 3
      filter: { category: { slug: { eq: $categorySlug } }, slug: { ne: $slug } }
    ) {
      nodes {
        title
        slug
        category {
          slug
        }
        summary {
          text: summary
        }
        formattedDate: date(formatString: "Do MMMM, YYYY")
      }
    }
    recentArticles: allContentfulBlogArticle(
      limit: 3
      filter: { slug: { ne: $slug } }
    ) {
      nodes {
        title
        slug
        category {
          slug
        }
        summary {
          text: summary
        }
        formattedDate: date(formatString: "Do MMMM, YYYY")
      }
    }
    contentfulBlogArticle(slug: { eq: $slug }) {
      title
      slug
      date
      formattedDate: date(formatString: "Do MMMM, YYYY")
      category {
        title
        slug
      }
      introduction {
        raw
      }
      coverImage {
        ...Asset
      }
      body {
        raw
        ## todo content...
        references {
          ... on Node {
            ... on ContentfulPageSectionFaq {
              __typename
              contentful_id
              question
              answer {
                childMarkdownRemark {
                  html
                }
              }
            }
            ... on ContentfulEmbedIframe {
              __typename
              contentful_id
              code {
                raw: code
              }
            }
            ... on ContentfulImageGallery {
              __typename
              contentful_id
              images {
                ...Asset
              }
              caption
            }
            ... on ContentfulAsset {
              ...Asset
            }
          }
        }
      }
    }
  }
`
